import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import { MatSelectChange } from '@angular/material/select';
import QpRangesModel from 'src/app/services/qpRange.model';
import QueryParamsModel from 'src/app/services/queryParams.model';
import CultureCategoryModel from 'src/app/services/cultureCategory.model';
import SpeciesTypeModel from 'src/app/services/speciesType.model';
import { QueryParamsService } from 'src/app/services/query-params.service';
import { CultureCategoryService } from 'src/app/services/cultureCategory.service';
import { SpeciesTypeService } from 'src/app/services/speciesType.service';
import { BatchLoader } from 'src/app/Utils/batch-loader.util';
import { QpRangeService } from 'src/app/services/qp-range.service';


@Component({
  selector: 'app-quality-parameter-ranges',
  templateUrl: './quality-parameter-ranges.component.html',
  styleUrls: ['./quality-parameter-ranges.component.scss']
})
export class QualityParameterRangesComponent implements OnInit, OnDestroy {
  public qpRanges: QpRangesModel[] = [];
  public qpRangesList: QpRangesModel[] = [];
  public qpRange: QpRangesModel = new QpRangesModel();
  public filteredQpRanges: QpRangesModel[] = [];
  public queryParams: Array<QueryParamsModel>;
  public cultureCategories: Array<CultureCategoryModel>;
  public speciesTypes: Array<SpeciesTypeModel>;
  public search: string;
  public minMaxvalidation: boolean;
  public submitButton: boolean;
  public updateButton: boolean;
  public QpRangesDoubleClick: boolean;
  public NoDataMessage: boolean = false;
  public clearSearchInput: boolean = false;
  public registerForm: FormGroup;
  public submitted = false;
  public enableDisablePageLoader: boolean = false;
  public listLoader = false;              // Show listLoader spinner
  public startIndex = 0;             // Tracks how much has been loaded
  public isFiltering: boolean = false;
  public noList: boolean = false;
  searchTimeout: any;
  public formFieldCount: number = 0;
  public showForm: boolean = false;

  constructor(private toastrService: ToastrService, public formBuilder: FormBuilder, public qpRangeService: QpRangeService, public queryParamsService: QueryParamsService, public cultureCategoryService: CultureCategoryService, public speciesTypeService: SpeciesTypeService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.qpRange = new QpRangesModel();
    this.registerForm = this.formBuilder.group({
      qp_id: ['', [Validators.required]],
      culture_category_id: ['', [Validators.required]],
      species_type_id: ['', [Validators.required]],
      min_acc_range: ['', [Validators.required, Validators.pattern('^[0-9]+$')]],
      max_acc_range: ['', [Validators.required, Validators.pattern('^[0-9]+$')]],
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  private batchLoader: BatchLoader<any>;
  public async ngOnInit() {
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.qpRanges = [];
    this.qpRangesList = [];
    this.filteredQpRanges = [];
    this.isFiltering = false;
    this.search = '';
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      await this.getQpRanges();
      this.queryParams = await this.queryParamsService.getAllQueryParam();
      this.cultureCategories = await this.cultureCategoryService.getAllCultureCategories();
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
    }
  }

  get f() {
    return this.registerForm.controls;
  }

  public async getQpRanges() {
    try {
      this.qpRanges = await this.qpRangeService.getAllQpRanges();
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.enableDisablePageLoader = false;
    }
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private scrollTimeout: any;
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredQpRanges : this.qpRanges,
      (updatedList) => {
        this.qpRangesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  //To cancel ongoing requests and unsubscribe from subscriptions.
  public ngOnDestroy(): void {
    this.qpRangeService.cancelOnGoingHttpRequests();
    this.queryParamsService.cancelOnGoingHttpRequests();
    this.cultureCategoryService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }

  /*get species types by culture category id*/
  async getSpeciesTypeByCultureCategoryId(event: MatSelectChange) {
    const cultureCategoryId = event.value;
    console.log('Culture Category ID:', cultureCategoryId);
    try {
      this.speciesTypes = await this.speciesTypeService.getSpeciesTypeByCultureCategoryId(cultureCategoryId);
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
    }
  }

  // search functionality

  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.qpRanges.filter(route =>
        route.qp.name?.toLowerCase().includes(query) ||
        route.cultureCategory.name?.toLowerCase().includes(query) ||
        route.speciesType.name?.toLowerCase().includes(query)
      )
      : this.qpRanges;
    this.filteredQpRanges = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.qpRangesList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  public clear() {
    this.registerForm.reset();
    this.showForm = false;
    this.qpRange = new QpRangesModel();
    this.QpRangesDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
  }

  /*Add QP Range*/
  public async addQPRange() {
    this.submitted = true;
    // Stop if invalid
    if (this.registerForm.invalid) return;
    const formData = this.registerForm.value;
    try {
      this.QpRangesDoubleClick = true;
      if (this.submitButton) {
        // Extract IDs
        const qp = formData.qp_id;
        const cc = formData.culture_category_id;
        const st = formData.species_type_id;
        // Check for duplicates
        const existing = this.qpRanges.find(o =>
          o.qp_id === qp &&
          o.culture_category_id === cc &&
          o.species_type_id === st
        );
        if (existing) {
          this.toastrService.warning('Range already available');
          this.registerForm.patchValue({
            qp_id: '',
            culture_category_id: '',
            species_type_id: '',
            min_acc_range: '',
            max_acc_range: ''
          });
          return;
        }
        await this.qpRangeService.create(formData);
        this.toastrService.success('QP range created successfully', '', { timeOut: 2000 });
        this.minMaxvalidation = false;
        await this.ngOnInit();
      } else {
        const updateQpRange = { ...formData };
        delete updateQpRange.id;
        await this.qpRangeService.updateOneByID(this.qpRange.id, updateQpRange);
        this.toastrService.success('QP range updated successfully', '', { timeOut: 2000 });
        this.qpRange = new QpRangesModel();
        await this.ngOnInit();
      }
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.QpRangesDoubleClick = false;
    }
  }


  /*Get qprange by id*/
  public async getQPRangeById(id: string) {
    this.submitButton = false;
    this.showForm = true;
    this.updateButton = true;
    this.QpRangesDoubleClick = false;
    this.speciesTypes = await this.speciesTypeService.getAllSpeciesType();
    const foundQpRange = this.qpRangesList.find(uom => uom?.id === id);
    this.qpRange = foundQpRange ? { ...foundQpRange } : new QpRangesModel(); // clone object
    if (this.qpRange) {
      this.registerForm.patchValue({
        qp_id: this.qpRange.qp_id,
        culture_category_id: this.qpRange.culture_category_id,
        species_type_id: this.qpRange.species_type_id,
        min_acc_range: this.qpRange.min_acc_range,
        max_acc_range: this.qpRange.max_acc_range
      });
    }
  }
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
